/**
 * Document Service
 * Handles document management and file storage API calls
 */

import { httpClient, ApiResponse } from './http.client';
import { authService } from './auth.service';
import { servicesConfig } from '../config/services.config';
import { fileStorageService, FileStorage } from './file-storage.service';

// Document type enums based on form data
export type DocumentCategory =
  | 'technical_drawings'
  | 'regulatory_approvals'
  | 'audit_documentation'
  | 'project_documents'
  | 'other';

export type DocumentType =
  // Technical Drawings
  | 'architectural_plan'
  | 'structural_drawing'
  | 'mep_drawing'
  | 'site_plan'
  | 'detail_drawing'
  | 'as_built'
  // Regulatory/Permits
  | 'building_permit'
  | 'environmental_clearance'
  | 'fire_safety'
  | 'occupancy_certificate'
  | 'compliance_certificate'
  | 'inspection_report'
  // Audit & Compliance
  | 'internal_audit'
  | 'external_audit'
  | 'compliance_report'
  | 'site_visit'
  | 'safety_report'
  | 'quality_report'
  // General
  | 'contract'
  | 'specification'
  | 'invoice'
  | 'photo'
  | 'report'
  | 'correspondence'
  | 'manual'
  | 'other';

export type ConfidentialityLevel =
  | 'public'
  | 'internal'
  | 'confidential'
  | 'restricted';

// File upload response from MinIO
export interface FileUploadResponse {
  file_id: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  object_key: string;  // MinIO path
  upload_date: string;
}

export interface Document {
  document_id: string;
  project_id: string;
  document_name: string;
  document_category: DocumentCategory;
  document_type: DocumentType;
  description?: string;

  // NEW: Reference to file storage
  storage_id?: string;

  // NEW: Populated file info (if storage_id exists)
  file_storage?: FileStorage;

  // LEGACY: Still present for backward compatibility
  file_id?: string;
  file_path?: string;
  file_size?: number;
  file_size_bytes?: number;
  mime_type?: string;
  file_extension?: string;

  // Version Control
  version: string;
  is_latest: boolean;
  previous_version_id?: string;

  // Access Control
  confidentiality_level: ConfidentialityLevel;
  is_public: boolean;
  uploaded_by: string;

  // Metadata
  tags?: string[];
  created_at: string;
  updated_at: string;
  status?: string;
}

export interface DocumentCreate {
  organization_id: string;
  project_id: string;
  title: string;  // Backend expects 'title', not 'document_name'
  document_number: string;  // Required by backend
  category?: DocumentCategory;  // Backend uses 'category', not 'document_category'
  document_type: DocumentType;
  description?: string;

  // NEW: Reference to uploaded file
  storage_id?: string;  // From file-storage upload step

  // LEGACY: Still supported for backward compatibility
  file_id?: string;
  file_path?: string;
  file_name?: string;
  file_size_bytes?: number;
  file_format?: string;

  version?: string;
  confidentiality_level?: ConfidentialityLevel;
  is_confidential?: boolean;
  tags?: string[];
  status?: string;
}

export interface DocumentUpdate {
  document_name?: string;
  document_category?: DocumentCategory;
  document_type?: DocumentType;
  description?: string;
  confidentiality_level?: ConfidentialityLevel;
  is_public?: boolean;
  tags?: string[];
}

export interface DocumentFilters {
  organization_id?: string;
  project_id?: string;
  document_type?: string;
  uploaded_by?: string;
  is_public?: boolean;
  tags?: string[];
  date_from?: string;
  date_to?: string;
  search?: string;
  category?: string;
  confidentiality_level?: string;
}

export interface DocumentVersion {
  document_id: string;
  version: string;
  file_path: string;
  file_size?: number;
  uploaded_by: string;
  upload_date: string;
  change_notes?: string;
}

class DocumentService {
  /**
   * STEP 1: Upload file to storage (NEW API)
   * Uses centralized file-storage service
   */
  async uploadFile(file: File, organizationId?: string): Promise<ApiResponse<{ storage_id: string }>> {
    const response = await fileStorageService.uploadFile(file, 'document', organizationId);

    if (response.success && response.data) {
      return {
        success: true,
        data: { storage_id: response.data.storage_id },
        message: 'File uploaded successfully',
        status: response.status
      };
    }

    return response as any;
  }

  /**
   * STEP 2: Create document record with storage reference
   * @param documentData - Document metadata with storage_id from upload step
   */
  async createDocument(documentData: DocumentCreate): Promise<ApiResponse<Document>> {
    return httpClient.post<Document>('/api/v1/documents', documentData);
  }

  /**
   * Combined upload and create (NEW TWO-STEP FLOW)
   * Performs both steps: upload file to storage, then create document
   */
  async uploadAndCreateDocument(
    file: File,
    metadata: Omit<DocumentCreate, 'storage_id'>,
    organizationId?: string
  ): Promise<ApiResponse<Document>> {
    // Step 1: Upload file to centralized storage
    const uploadResponse = await fileStorageService.uploadFile(file, 'document', organizationId);

    if (!uploadResponse.success || !uploadResponse.data) {
      throw new Error('File upload failed');
    }

    // Step 2: Create document record with storage_id
    const documentData: DocumentCreate = {
      ...metadata,
      storage_id: uploadResponse.data.storage_id
    };

    return this.createDocument(documentData);
  }

  /**
   * Get all documents for an organization
   */
  async getDocuments(page = 1, perPage = 20, filters?: DocumentFilters): Promise<ApiResponse<{ data: Document[]; total: number; page: number; per_page: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString()
    });

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          if (Array.isArray(value)) {
            value.forEach(item => queryParams.append(key, item.toString()));
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });
    }

    return httpClient.get<{ data: Document[]; total: number; page: number; per_page: number }>(
      `/api/v1/documents?${queryParams}`
    );
  }

  /**
   * Get documents for a specific project
   */
  async getProjectDocuments(projectId: string, page = 1, perPage = 50): Promise<ApiResponse<{ data: Document[]; total: number }>> {
    const queryParams = new URLSearchParams({
      project_id: projectId,
      page: page.toString(),
      per_page: perPage.toString()
    });

    return httpClient.get<{ data: Document[]; total: number }>(
      `/api/v1/documents?${queryParams}`
    );
  }

  /**
   * Get single document by ID
   */
  async getDocument(documentId: string): Promise<ApiResponse<Document>> {
    return httpClient.get<Document>(`/api/v1/documents/${documentId}`);
  }

  /**
   * Download document file by document_id (NEW API)
   * Automatically uses storage_id if available, falls back to legacy
   */
  async downloadDocument(documentId: string): Promise<ApiResponse<Blob>> {
    // Try storage_id path first (fast path, no extra GET)
    const docResponse = await this.getDocument(documentId);
    if (docResponse.success && docResponse.data?.storage_id) {
      return fileStorageService.downloadFile(docResponse.data.storage_id);
    }

    // Fall back to direct document download endpoint for all other cases
    const token = authService.getAccessToken();
    const baseURL = servicesConfig.backend.baseUrl;
    const response = await fetch(`${baseURL}/api/v1/documents/${documentId}/download`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`);
    }

    const blob = await response.blob();
    return { success: true, data: blob, message: 'Download successful', status: response.status };
  }

  /**
   * Preview document file inline by document_id (NEW API)
   * Automatically uses storage_id if available, falls back to legacy
   */
  async previewDocument(documentId: string): Promise<ApiResponse<Blob>> {
    const docResponse = await this.getDocument(documentId);
    if (docResponse.success && docResponse.data?.storage_id) {
      return fileStorageService.previewFile(docResponse.data.storage_id);
    }

    // Fall back to direct document preview endpoint (inline Content-Disposition)
    const token = authService.getAccessToken();
    const baseURL = servicesConfig.backend.baseUrl;
    const response = await fetch(`${baseURL}/api/v1/documents/${documentId}/preview`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      throw new Error(`Preview failed: ${response.statusText}`);
    }

    const blob = await response.blob();
    return { success: true, data: blob, message: 'Preview successful', status: response.status };
  }

  /**
   * Share document - generate presigned URL
   */
  async shareDocument(documentId: string, expiryDays = 7): Promise<ApiResponse<{ share_url: string; expires_at: string }>> {
    return httpClient.post<{ share_url: string; expires_at: string }>(`/api/v1/documents/${documentId}/share`, {
      expiry_days: expiryDays
    });
  }

  /**
   * Upload new version of existing document
   */
  async uploadNewVersion(documentId: string, file: File, changeNotes?: string): Promise<ApiResponse<Document>> {
    const formData = new FormData();
    formData.append('file', file);
    
    if (changeNotes) {
      formData.append('change_notes', changeNotes);
    }

    return httpClient.post<Document>(`/api/v1/documents/${documentId}/versions`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  /**
   * Update document metadata
   */
  async updateDocument(documentId: string, documentData: DocumentUpdate): Promise<ApiResponse<Document>> {
    return httpClient.put<Document>(`/api/v1/documents/${documentId}`, documentData);
  }

  /**
   * Delete document
   */
  async deleteDocument(documentId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/documents/${documentId}`);
  }

  /**
   * Get file preview URL using file_id
   */
  async getFilePreview(fileId: string): Promise<ApiResponse<{ preview_url: string }>> {
    return httpClient.get<{ preview_url: string }>(`/api/v1/files/preview/${fileId}`);
  }

  /**
   * Get document versions
   */
  async getDocumentVersions(documentId: string): Promise<ApiResponse<DocumentVersion[]>> {
    return httpClient.get<DocumentVersion[]>(`/api/v1/documents/${documentId}/versions`);
  }

  /**
   * Restore document version
   */
  async restoreDocumentVersion(documentId: string, version: string): Promise<ApiResponse<Document>> {
    return httpClient.post<Document>(`/api/v1/documents/${documentId}/restore`, { version });
  }

  /**
   * Get documents by type
   */
  async getDocumentsByType(documentType: string, projectId?: string): Promise<ApiResponse<Document[]>> {
    const queryParams = new URLSearchParams({ document_type: documentType });
    if (projectId) {
      queryParams.append('project_id', projectId);
    }
    return httpClient.get<Document[]>(`/api/v1/documents/by-type?${queryParams}`);
  }

  /**
   * Search documents
   */
  async searchDocuments(query: string, projectId?: string, documentType?: string): Promise<ApiResponse<Document[]>> {
    const queryParams = new URLSearchParams({ q: query });
    if (projectId) {
      queryParams.append('project_id', projectId);
    }
    if (documentType) {
      queryParams.append('document_type', documentType);
    }
    return httpClient.get<Document[]>(`/api/v1/documents/search?${queryParams}`);
  }

  /**
   * Get documents by tags
   */
  async getDocumentsByTags(tags: string[], projectId?: string): Promise<ApiResponse<Document[]>> {
    const queryParams = new URLSearchParams();
    tags.forEach(tag => queryParams.append('tags', tag));
    if (projectId) {
      queryParams.append('project_id', projectId);
    }
    return httpClient.get<Document[]>(`/api/v1/documents/by-tags?${queryParams}`);
  }

  /**
   * Get all available tags
   */
  async getDocumentTags(projectId?: string): Promise<ApiResponse<string[]>> {
    const queryParams = projectId ? `?project_id=${projectId}` : '';
    return httpClient.get<string[]>(`/api/v1/documents/tags${queryParams}`);
  }

  /**
   * Bulk upload documents
   */
  async bulkUploadDocuments(files: File[], projectId: string, documentType: string): Promise<ApiResponse<{ 
    successful: Document[];
    failed: Array<{ filename: string; error: string }>;
  }>> {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append(`files`, file);
    });
    formData.append('project_id', projectId);
    formData.append('document_type', documentType);

    return httpClient.post<{ 
      successful: Document[];
      failed: Array<{ filename: string; error: string }>;
    }>('/api/v1/documents/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  /**
   * Bulk delete documents
   */
  async bulkDeleteDocuments(documentIds: string[]): Promise<ApiResponse<void>> {
    return httpClient.post<void>('/api/v1/documents/bulk-delete', {
      document_ids: documentIds
    });
  }

  /**
   * Revoke document share
   */
  async revokeDocumentShare(documentId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/documents/${documentId}/share`);
  }

  /**
   * Get document analytics
   */
  async getDocumentAnalytics(projectId: string): Promise<ApiResponse<{
    total_documents: number;
    total_size: number;
    documents_by_type: { [key: string]: number };
    recent_uploads: number;
    version_count: number;
    public_documents: number;
  }>> {
    return httpClient.get<{
      total_documents: number;
      total_size: number;
      documents_by_type: { [key: string]: number };
      recent_uploads: number;
      version_count: number;
      public_documents: number;
    }>(`/api/v1/documents/analytics?project_id=${projectId}`);
  }

  /**
   * Generate document report
   */
  async generateDocumentReport(projectId: string, format: 'pdf' | 'excel' = 'pdf'): Promise<ApiResponse<{ download_url: string }>> {
    return httpClient.post<{ download_url: string }>('/api/v1/documents/report', {
      project_id: projectId,
      format
    });
  }

  /**
   * Check document permissions
   */
  async checkDocumentPermissions(documentId: string): Promise<ApiResponse<{
    can_view: boolean;
    can_edit: boolean;
    can_delete: boolean;
    can_share: boolean;
  }>> {
    return httpClient.get<{
      can_view: boolean;
      can_edit: boolean;
      can_delete: boolean;
      can_share: boolean;
    }>(`/api/v1/documents/${documentId}/permissions`);
  }

  /**
   * Get document activity log
   */
  async getDocumentActivity(documentId: string): Promise<ApiResponse<Array<{
    action: string;
    user_id: string;
    user_name: string;
    timestamp: string;
    details?: string;
  }>>> {
    return httpClient.get<Array<{
      action: string;
      user_id: string;
      user_name: string;
      timestamp: string;
      details?: string;
    }>>(`/api/v1/documents/${documentId}/activity`);
  }

  /**
   * Set document retention policy
   */
  async setDocumentRetention(documentId: string, retentionDays: number): Promise<ApiResponse<void>> {
    return httpClient.post<void>(`/api/v1/documents/${documentId}/retention`, {
      retention_days: retentionDays
    });
  }

  /**
   * Get documents requiring review
   */
  async getDocumentsForReview(projectId?: string): Promise<ApiResponse<Document[]>> {
    const queryParams = projectId ? `?project_id=${projectId}` : '';
    return httpClient.get<Document[]>(`/api/v1/documents/review-required${queryParams}`);
  }
}

// Export singleton instance
export const documentService = new DocumentService();